import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import React, { FC, useState, useEffect } from 'react';

import {
  DayPickerSingleDateController,
  DayPickerSingleDateControllerShape,
} from 'react-dates';
import moment, { Moment } from 'moment';

import 'moment/locale/tr';
import { useBreakpoint } from '@contexts/breakpoints';
import { ChevronLeft, ChevronRight } from '@assets/icons';
import { formatLocalDate, formatMomentDate } from '@utils/dateUtil';
import { Row } from '@components/formStyles';

import { renderCalendarDay } from '../CalendarDayText';
import { renderNavButton } from '../renderNavButton';
import { useCalendarReadiness } from '../useCalendarReadiness';
import {
  DatePickerDayOfWeekLabel,
  DatePickerMonthLabel,
  DatePickerWrapper,
  DateRangePickerFooter,
  NavNextButton,
  NavPrevButton,
  StyledButton,
} from '../DatePickerStyle';

type RequiredProps = 'focused' | 'onFocusChange' | 'initialVisibleMonth';

export type DayPickerSingleDateControllerProps = Omit<
  DayPickerSingleDateControllerShape,
  RequiredProps | 'renderMonthText'
> &
  Partial<Pick<DayPickerSingleDateControllerShape, RequiredProps>> & {
    onReady?: () => void;
    useDesktopSearchbarFooter?: boolean;
  };

const SingleDatePicker: FC<DayPickerSingleDateControllerProps> = (props) => {
  const {
    focused = false,
    onFocusChange = () => false,
    date = null,
    onDateChange = () => false,
    isOutsideRange = (day: Moment) => day < moment().startOf('day'),
    renderNavPrevButton = renderNavButton(NavPrevButton, ChevronLeft),
    renderNavNextButton = renderNavButton(NavNextButton, ChevronRight),
    initialVisibleMonth = null,
    orientation = 'horizontal',
    withPortal = false,
    numberOfMonths = 2,
    keepOpenOnDateSelect = false,
    enableOutsideDays = false,
    hideKeyboardShortcutsPanel = true,
    monthFormat = 'MMMM YYYY',
    onReady,
    useDesktopSearchbarFooter = false,
    ...rest
  } = props;
  const { xl } = useBreakpoint();
  const readinessRef = useCalendarReadiness(onReady);
  const [drawerContentHeight, setDrawerContentHeight] = useState<number | null>(
    null
  );
  const findDrawerContentHeight = () => {
    if (typeof window !== 'undefined') {
      const el = document.getElementsByClassName('setur-drawer-body')[0];
      return el?.clientHeight;
    }
  };

  useEffect(() => {
    setDrawerContentHeight(findDrawerContentHeight() as number);
  }, [drawerContentHeight]);

  return (
    <DatePickerWrapper
      ref={onReady ? readinessRef : undefined}
      contentHeight={drawerContentHeight as number}
      $desktopSearchbarFooter={useDesktopSearchbarFooter}
    >
      <DayPickerSingleDateController
        renderMonthElement={({ month }) => (
          <DatePickerMonthLabel>
            {moment(month).locale('tr').format('MMMM YYYY')}
          </DatePickerMonthLabel>
        )}
        weekDayFormat='d'
        renderWeekHeaderElement={(dayOfWeek) => (
          <DatePickerDayOfWeekLabel>
            {moment().day(parseInt(dayOfWeek, 10)).locale('tr').format('dd')}
          </DatePickerDayOfWeekLabel>
        )}
        initialVisibleMonth={initialVisibleMonth}
        focused={focused}
        onFocusChange={onFocusChange}
        date={date}
        onDateChange={onDateChange}
        isOutsideRange={isOutsideRange}
        renderNavPrevButton={renderNavPrevButton}
        renderNavNextButton={renderNavNextButton}
        orientation={orientation}
        withPortal={withPortal}
        numberOfMonths={numberOfMonths}
        keepOpenOnDateSelect={keepOpenOnDateSelect}
        enableOutsideDays={enableOutsideDays}
        hideKeyboardShortcutsPanel={hideKeyboardShortcutsPanel}
        monthFormat={monthFormat}
        renderDayContents={renderCalendarDay}
        {...rest}
      />
      {xl && (date || useDesktopSearchbarFooter) ? (
        <DateRangePickerFooter
          $desktopSearchbarFooter={useDesktopSearchbarFooter}
        >
          {!useDesktopSearchbarFooter ? (
            <Row>
              <span>{formatLocalDate(formatMomentDate(date))}</span>
            </Row>
          ) : null}
          <StyledButton
            disabled={!date}
            onClick={() => {
              if (!date) return;
              props.onClose?.({ date });
            }}
            $desktopSearchbarFooter={useDesktopSearchbarFooter}
          >
            Uygula
          </StyledButton>
        </DateRangePickerFooter>
      ) : null}
    </DatePickerWrapper>
  );
};

export default SingleDatePicker;
